import styled from "@emotion/styled";
import { ExperimentVariant } from "@gumtree/shared/src/model/experiment";
import {
    boxSizes,
    colors,
    fontSizes,
    fontWeights,
    gutterSizes,
    lineHeights,
    zIndexes,
} from "@gumtree/ui-library/src/base/theme";

export const Wrapper = styled("nav", {
    shouldForwardProp: (prop) => prop !== "isSticky" && prop !== "variant",
})<{ isSticky?: boolean; variant?: ExperimentVariant }>`
    ${({ variant }) =>
        variant === ExperimentVariant.VARIANT_B
            ? `background: ${colors.blade};`
            : `background: ${colors.white};`}
    ${({ isSticky }) => isSticky && "min-height: 43px;"}
`;

export const TagShell = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
    padding: ${gutterSizes.large} 0 ${gutterSizes.large} ${gutterSizes.large};
    border-top-left-radius: ${boxSizes.borderRadius16};
    border-top-right-radius: ${boxSizes.borderRadius16};
    background-color: ${colors.white};
`;

export const HorizontalList = styled.div`
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    padding-right: ${gutterSizes.large};

    &::-webkit-scrollbar {
        display: none;
    }
`;

export const TagLink = styled.a<{ selected?: boolean }>`
    border: ${boxSizes.borderWidth} solid ${colors.tagBlack};
    border-radius: 999px;
    color: ${colors.tagBlack};
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    font-size: ${fontSizes.smaller};
    font-weight: ${fontWeights.normal};
    line-height: 18px;
    padding: ${gutterSizes.base} ${gutterSizes.medium};
    text-decoration: none;

    ${({ selected }) =>
        selected &&
        `
        background: rgba(37, 157, 111, 0.10);
        border-color: #259D6F;
        color: #259D6F;
        font-weight: ${fontWeights.bold};
    `}
`;

export const AllCategoriesButton = styled.button`
    background: ${colors.white};
    border: 0;
    clip-path: inset(0 0 0 -8px);
    box-shadow: 1px 0 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${gutterSizes.medium10} 17px ${gutterSizes.medium10} ${gutterSizes.base};
`;

export const AllCategoriesIcon = styled.img`
    display: block;
    height: ${boxSizes.boxHeight24};
    width: ${boxSizes.boxHeight24};
`;

export const StickyShell = styled.div`
    align-items: center;
    background: ${colors.white};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    height: 44px;
    left: 0;
    position: absolute;
    right: 0;
    top: 100%;
    z-index: ${zIndexes.high};
`;

export const StickyList = styled(HorizontalList)`
    flex: 1 1 auto;
    gap: 6px;
    padding: 0 ${gutterSizes.medium};
`;

export const StickyLink = styled.a<{ selected?: boolean }>`
    color: #292929;
    flex: 0 0 auto;
    font-size: ${fontSizes.small};
    line-height: ${lineHeights.small};
    padding: 5px ${gutterSizes.base};
    text-decoration: none;

    ${({ selected }) => selected && `font-weight: ${fontWeights.bold};`}
`;

export const IconGrid = styled.div`
    display: flex;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    padding: 12px 6px 12px 0;
    background-color: ${colors.white};

    &::-webkit-scrollbar {
        display: none;
    }
`;

export const IconTile = styled.a`
    align-items: center;
    color: ${colors.tagBlack};
    display: flex;
    flex-direction: column;
    font-size: ${fontSizes.xsmall};
    font-weight: ${fontWeights.normal};
    line-height: 18px;
    text-align: center;
    text-decoration: none;
    white-space: nowrap;
    flex-shrink: 0;
    width: calc((100vw - 12px) / 5.5);
    min-width: calc(62 * 100vw / 375);
`;

export const IconButton = styled.button`
    align-items: center;
    background: transparent;
    border: 0;
    color: ${colors.tagBlack};
    display: flex;
    flex-direction: column;
    font-size: ${fontSizes.xsmall};
    line-height: 18px;
    padding: 0;
    text-align: center;
    white-space: nowrap;
    flex-shrink: 0;
    width: calc((100vw - 12px) / 5.5);
    min-width: calc(62 * 100vw / 375);
`;

export const TileImage = styled.img`
    height: 40px;
    object-fit: contain;
    width: 40px;
    padding: 3px;
    border-radius: 50%;
    box-sizing: content-box;
`;

export const DrawerOverlay = styled.button`
    background: rgba(0, 0, 0, 0.45);
    border: 0;
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: ${zIndexes.highest - 1};
`;

export const Drawer = styled.aside<{ fullScreen: boolean }>`
    background: ${colors.white};
    bottom: 0;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.12);
    position: fixed;
    right: 0;
    top: 0;
    width: ${({ fullScreen }) => (fullScreen ? "100vw" : "280px")};
    z-index: ${zIndexes.highest};
`;

export const DrawerContent = styled.div`
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    overscroll-behavior-y: contain;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding-bottom: 56px;
`;

export const DrawerHeader = styled.div`
    align-items: center;
    display: flex;
    min-height: 80px;
    padding: 0 ${gutterSizes.large};
    gap: ${gutterSizes.medium};
`;

export const DrawerTitle = styled.h2`
    color: ${colors.bark};
    flex: 1 1 auto;
    font-size: ${fontSizes.header};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.base24};
    margin: 0;
`;

export const IconOnlyButton = styled.button`
    align-items: center;
    background: transparent;
    border: 0;
    display: flex;
    height: 32px;
    justify-content: center;
    padding: 0;
    width: 32px;
`;

export const CategoryRow = styled.button<{ expanded?: boolean }>`
    align-items: center;
    background: ${colors.white};
    border: 0;
    border-bottom: ${({ expanded }) =>
        expanded ? `${boxSizes.borderWidth} solid ${colors.bark20}` : 0};
    color: ${colors.bark};
    display: flex;
    font-size: ${fontSizes.base};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.base24};
    min-height: 56px;
    padding: ${gutterSizes.large};
    text-align: left;
    width: 100%;
`;

export const TreeIcon = styled.img`
    height: ${boxSizes.boxHeight24};
    margin-right: ${gutterSizes.base};
    width: ${boxSizes.boxHeight24};
`;

export const RowChevron = styled.div<{ expanded?: boolean }>`
    height: ${boxSizes.boxHeight24};
    margin-left: auto;
    transform: ${({ expanded }) => (expanded ? "rotate(90deg)" : "none")};
    transition: transform 0.3s ease-in-out;
    width: ${boxSizes.boxHeight24};
`;

export const SubcategoryDrawer = styled.div`
    background: #f7f7fa;
`;

export const SubcategoryLink = styled.a`
    background: #f7f7fa;
    color: ${colors.bark};
    display: block;
    font-size: ${fontSizes.base};
    line-height: ${lineHeights.base24};
    padding: ${gutterSizes.base} ${gutterSizes.large20};
    text-decoration: none;
`;

export const StickySentinel = styled.div`
    height: 0px;
    background-color: ${colors.white};
`;

// padding: 8px 0 12px;
export const PageIndicator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    background-color: ${colors.white};
`;

export const PageDot = styled.span<{ active?: boolean }>`
    width: ${({ active }) => (active ? "12px" : "4px")};
    height: 4px;
    border-radius: 50px;
    background-color: ${({ active }) => (active ? "#101824" : "#d4d4d8")};
    transition: all 0.2s ease;
`;

export const NavigationContainer = styled.div<{ variant: ExperimentVariant }>`
    margin-bottom: ${({ variant }) =>
        variant === ExperimentVariant.VARIANT_C ? gutterSizes.large : 0};
    background-color: ${({ variant }) =>
        variant === ExperimentVariant.VARIANT_C ? colors.white : colors.blade};
`;
